import type { TaskLink, TaskPriority, TaskStatus } from './tasks.js';

/** Core domain types. Every one of these crosses the wire, so keep them JSON-safe. */

export type Facing = 'up' | 'down' | 'left' | 'right';

/**
 * Two roles, deliberately. Every additional role is a new set of questions
 * about what it can do, and an office this size does not need them.
 */
export type Role = 'admin' | 'member';

/**
 * Who may enter a room.
 *
 * `minRole` is the floor — `admin` makes a room staff-only. `restricted` adds
 * an allowlist on top: when true, only named members (and admins, always) get
 * in. Both can apply at once.
 */
export interface RoomPolicy {
  zoneId: string;
  minRole: Role;
  restricted: boolean;
  /** User ids allowed in when `restricted`. Empty means admins only. */
  members: string[];
}

/** A room as the client sees it, with the viewer's own access resolved. */
export interface RoomAccess {
  zoneId: string;
  minRole: Role;
  restricted: boolean;
  /** Whether *you* can walk in. The server decides; the client only renders it. */
  allowed: boolean;
}

/** Semantic tile ids. Renderers map these to colours or sprites. */
export const Tile = {
  Void: 0,
  Floor: 1,
  Carpet: 2,
  Wood: 3,
  Rug: 4,

  Wall: 10,
  Door: 11,
  Window: 12,

  Desk: 20,
  Table: 21,
  Chair: 22,
  Plant: 23,
  Whiteboard: 24,
  NoteBoard: 25,
  Couch: 26,
  Screen: 27,
  Counter: 28,
} as const;

export type TileId = (typeof Tile)[keyof typeof Tile];

/** Tiles you cannot walk through. Doors and chairs are deliberately walkable. */
export const SOLID_TILES: ReadonlySet<number> = new Set<number>([
  Tile.Void,
  Tile.Wall,
  Tile.Window,
  Tile.Desk,
  Tile.Table,
  Tile.Plant,
  Tile.Whiteboard,
  Tile.NoteBoard,
  Tile.Couch,
  Tile.Screen,
  Tile.Counter,
]);

/** A rectangle in tile coordinates. */
export interface TileRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * An interactive thing mounted in the world.
 *
 * `noteboard` is the only kind implemented in phase 1; `whiteboard` and
 * `screen` are declared now so maps can be authored once and light up as the
 * corresponding features land.
 */
export type SurfaceKind = 'noteboard' | 'whiteboard' | 'screen';

export interface Surface {
  id: string;
  kind: SurfaceKind;
  label: string;
  zoneId: string;
  /** Where the surface sits, in tiles. Used for the "press E" proximity check. */
  rect: TileRect;
  /** Which side a player stands on to use it. */
  facing: Facing;
}

export type ZoneKind = 'commons' | 'meeting' | 'quiet' | 'social' | 'workshop' | 'library';

/** How voices carry in a zone once voice chat exists. */
export type AudioMode = 'proximity' | 'zone';

export interface Zone {
  id: string;
  name: string;
  kind: ZoneKind;
  rect: TileRect;
  /** Zone chat is persisted under this channel id. */
  channelId: string;
  audio: {
    mode: AudioMode;
    /** Soft cap surfaced in the UI; not enforced in phase 1. */
    capacity?: number;
  };
  /** Zone-scoped blurb shown when you walk in. */
  description?: string;
}

export interface OfficeMap {
  id: string;
  name: string;
  /** Width and height in tiles. */
  width: number;
  height: number;
  tileSize: number;
  /** Row-major tile ids, length === width * height. */
  ground: number[];
  /** Row-major furniture ids laid over the ground. 0 means "nothing here". */
  decor: number[];
  /** Spawn point in tile coordinates. */
  spawn: { x: number; y: number };
  zones: Zone[];
  surfaces: Surface[];
}

/**
 * How someone's character looks on the map.
 *
 * Indices into the palettes in constants.ts rather than colour strings, so an
 * appearance is a handful of small integers on the wire and a palette can be
 * retuned later without rewriting everyone's saved character.
 */
export interface Appearance {
  /** Index into SKIN_TONES. */
  skin: number;
  /** Index into HAIR_STYLES. */
  hair: number;
  /** Index into HAIR_COLORS. */
  hairColor: number;
  /** Index into CLOTHING_COLORS. */
  shirt: number;
  /** Index into CLOTHING_COLORS. */
  pants: number;
}

export interface Player {
  id: string;
  name: string;
  color: string;
  role: Role;
  appearance: Appearance;
  /**
   * Bumped whenever the portrait changes. The image lives at
   * /avatars/<id>.png; this is the cache-buster, and 0 means no upload yet.
   */
  avatarVersion: number;
  /**
   * Departments and teams, for the roster. Personal tags are never included —
   * they are private to whoever made them, so putting them on a Player would
   * broadcast their existence to the whole office.
   */
  groups: Array<{ slug: string; name: string; kind: GroupKind }>;
  /** Pixel position of the avatar's centre. */
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
  /** Zone the avatar currently stands in, or null when between zones. */
  zoneId: string | null;
  /** Free-text status ("heads down", "coffee run"). */
  status: string;
}

/** The slim per-tick form of a player, used in snapshots. */
export interface PlayerState {
  id: string;
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
  zoneId: string | null;
}

/**
 * Chat scope.
 * - `office`: everyone, persisted.
 * - `zone`: everyone in the sender's room, persisted per room.
 * - `local`: everyone within PROXIMITY_RADIUS, not persisted.
 */
export type ChatScope = 'office' | 'zone' | 'local';

export interface ChatMessage {
  id: string;
  /** Persistence key: `office`, `zone:<zoneId>`, or `local` for ephemeral messages. */
  channelId: string;
  scope: ChatScope;
  authorId: string;
  authorName: string;
  authorColor: string;
  body: string;
  /** Epoch milliseconds. */
  createdAt: number;
}

export interface Note {
  id: string;
  surfaceId: string;
  title: string;
  body: string;
  color: string;
  authorId: string;
  authorName: string;
  createdAt: number;
  updatedAt: number;
  /** Bumped on every edit so late writes can be rejected. */
  revision: number;
}

/**
 * What the server can actually do right now. The client hides UI for anything
 * switched off, so shipping a new feature is a server-side flag plus a handler.
 */
export interface Capabilities {
  textChat: boolean;
  notes: boolean;
  voice: boolean;
  video: boolean;
  screenShare: boolean;
  whiteboard: boolean;
  /** Whether the server can actually send invite emails, or only print links. */
  email: boolean;
  /** Declared now so the login screen can branch once the flow exists. */
  twoFactor: boolean;
}

/* -------------------------------------------------------------------------- */
/* Groups                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Departments, teams and tags are the same thing: a named set of people.
 *
 * Modelling them separately would mean three near-identical tables and three
 * near-identical admin screens to maintain in step. The kind only changes how
 * a group is labelled and where it appears, never what it is.
 */
export type GroupKind = 'department' | 'team' | 'tag';

export interface Group {
  id: string;
  /** What you type after the @. Lowercase, no spaces. */
  slug: string;
  name: string;
  kind: GroupKind;
  /**
   * Null for global groups everyone can use. Set for a personal tag, which is
   * private to its owner — nobody else can see it exists.
   */
  ownerId: string | null;
  memberIds: string[];
}

/** Reserved: resolves to everybody, so no admin has to maintain it. */
export const EVERYONE_SLUG = 'everyone';

/**
 * Slugify a group name into something typeable after an @.
 *
 * Shared so the client previews exactly the slug the server will store, rather
 * than the two disagreeing about what happens to "R&D — Platform".
 */
export function slugifyGroup(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

/**
 * Find @mentions in a message.
 *
 * Shared so the client can highlight exactly what the server will act on.
 * Requires a boundary before the @ so an email address is not a mention.
 */
export function parseMentions(body: string): string[] {
  const found = new Set<string>();
  for (const match of body.matchAll(/(?:^|[\s(\[{,:;])@([a-z0-9][a-z0-9-]{0,31})/gi)) {
    const slug = match[1]?.toLowerCase();
    if (slug) found.add(slug);
  }
  return [...found];
}

/* -------------------------------------------------------------------------- */
/* Tasks                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A unit of work.
 *
 * Names are denormalised onto the task — `assigneeName` as well as
 * `assigneeId` — so a list renders without the client holding every account.
 * They are a snapshot of the name at read time, refreshed on each fetch.
 */
export interface Task {
  id: string;
  /** Short, incrementing, human-quotable: "have you done 42?" */
  number: number;
  title: string;
  description: string;
  /** Admin-managed; null when uncategorised. */
  categoryId: string | null;
  categoryName: string;
  status: TaskStatus;
  priority: TaskPriority;

  /** Who should do it. */
  assigneeId: string | null;
  assigneeName: string;
  /** Who asked for it. Never null: something created every task. */
  assignerId: string | null;
  assignerName: string;
  /** Who signs it off, if anyone needs to. */
  approverId: string | null;
  approverName: string;
  approvedAt: number | null;

  /** The team or department it belongs to, if any. */
  groupId: string | null;
  groupName: string;

  dueAt: number | null;
  links: TaskLink[];

  /** Tasks this one waits for. */
  dependsOn: string[];
  /** Of those, the ones not yet done or cancelled — so this is really stuck. */
  blockedBy: TaskBlocker[];

  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
}

/**
 * An admin-managed category.
 *
 * Archived ones stay readable on the tasks that already use them but are no
 * longer offered when categorising something new.
 */
export interface TaskCategory {
  id: string;
  name: string;
  archived: boolean;
  /** How many tasks use it — shown so an admin can retire safely. */
  taskCount: number;
}

/** Just enough of a blocking task to name it without fetching the whole thing. */
export interface TaskBlocker {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Why somebody is being told something.
 *
 * Adding a reason is a new member here plus a line in the renderer; the table
 * and the delivery path do not change.
 */
export type NotificationKind =
  | 'mention'
  | 'task-assigned'
  | 'task-approval'
  | 'task-approved'
  | 'task-unblocked';

export interface Notification {
  id: string;
  kind: NotificationKind;
  actorName: string;
  body: string;
  /** Where it happened — a room name, so you know where to go. */
  context: string;
  /** Which group caught you, or empty when you were named directly. */
  via: string;
  /** Task this is about, so the client can open it. */
  taskId: string | null;
  createdAt: number;
  readAt: number | null;
}

/* -------------------------------------------------------------------------- */
/* Accounts                                                                    */
/* -------------------------------------------------------------------------- */

/** The signed-in user, as returned by the HTTP auth endpoints. */
export interface Account {
  id: string;
  email: string;
  name: string;
  color: string;
  role: Role;
  createdAt: number;
  twoFactorEnabled: boolean;
  appearance: Appearance;
  avatarVersion: number;
}

export interface Invite {
  id: string;
  email: string;
  role: Role;
  invitedByName: string;
  createdAt: number;
  expiresAt: number;
  acceptedAt: number | null;
  /** Only returned right after creation, so an admin can pass it on by hand. */
  url?: string;
}

/* -------------------------------------------------------------------------- */
/* Appearance helpers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Coerce anything into a valid Appearance.
 *
 * Indices arrive from a client and are read back out of a JSONB column, so
 * both are untrusted. Wrapping rather than rejecting means a stale index from
 * an older palette still renders as *something* instead of a blank character.
 */
export function clampAppearance(value: unknown, sizes: {
  skin: number;
  hair: number;
  hairColor: number;
  shirt: number;
  pants: number;
}): Appearance {
  const source = (value ?? {}) as Partial<Record<keyof Appearance, unknown>>;
  const at = (key: keyof Appearance, size: number): number => {
    const raw = source[key];
    const n = typeof raw === 'number' && Number.isFinite(raw) ? Math.floor(raw) : 0;
    return ((n % size) + size) % size;
  };
  return {
    skin: at('skin', sizes.skin),
    hair: at('hair', sizes.hair),
    hairColor: at('hairColor', sizes.hairColor),
    shirt: at('shirt', sizes.shirt),
    pants: at('pants', sizes.pants),
  };
}
